/**
 * 翻译工具函数
 * 使用 Google Translation API 进行翻译（繁体中文 → 英文）
 */

/**
 * 解析 POST /v1/common/translation/center 的批量返回体（兼容 code、data 嵌套与译文字段命名变更）
 * @param {unknown} data - 接口 JSON（与 axios 拦截器返回的 body 一致）
 * @param {string[]} texts - 请求时的原文数组，用于对齐下标与失败回退
 * @returns {{ ok: boolean, results: string[] }}
 */
export function parseTranslationCenterResponse(data, texts) {
	const originals = Array.isArray(texts) ? texts : [];
	const fallback = () => originals.slice();

	if (!data || typeof data !== "object") {
		return { ok: false, results: fallback() };
	}

	const code = data.code;
	const success =
		code === 200 || code === 1 || code === "200" || code === "1" || data.success === true;
	if (!success) {
		return { ok: false, results: fallback() };
	}

	const payload = data.data;
	let rows = [];
	if (Array.isArray(payload)) {
		rows = payload;
	} else if (payload && typeof payload === "object") {
		if (Array.isArray(payload.list)) rows = payload.list;
		else if (Array.isArray(payload.items)) rows = payload.items;
		else if (Array.isArray(payload.translations)) rows = payload.translations;
		else if (Array.isArray(payload.results)) rows = payload.results;
		else if (Array.isArray(payload.data)) rows = payload.data;
	}
	if (rows.length === 0 && Array.isArray(data.list)) {
		rows = data.list;
	}

	if (!Array.isArray(rows) || rows.length === 0) {
		return { ok: false, results: fallback() };
	}

	const pickLine = item => {
		if (item == null) return "";
		if (typeof item === "string") return item;
		if (typeof item !== "object") return "";
		const candidates = [
			item.translatedText,
			item.translated_text,
			item.target,
			item.translated,
			item.translation,
			item.text,
		];
		for (const c of candidates) {
			if (typeof c === "string" && c.length) return c;
		}
		return "";
	};

	const results = originals.map((orig, index) => {
		const line = pickLine(rows[index]);
		return line || orig || "";
	});

	return { ok: true, results };
}

/**
 * 翻译文本（繁体中文 → 英文）
 * @param {string|string[]} text - 要翻译的文本或文本数组
 * @param {{ format?: 'text' | 'html' }} [options] - 可选，format 为 'html' 时按富文本翻译
 * @returns {Promise<string|string[]>} 翻译结果
 */
export async function translateText(text, options = {}) {
	if (!import.meta.client) {
		return Array.isArray(text) ? text : text;
	}

	const { format = "text" } = options;

	try {
		const texts = Array.isArray(text) ? text : [text];
		if (texts.length === 0) return text;

		const { $axios } = useNuxtApp();
		const data = await $axios({
			url: "/v1/common/translation/center",
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			data: {
				format,
				lang: "en", // 目标语言：英文
				text: texts,
			},
		});

		const { ok, results } = parseTranslationCenterResponse(data, texts);
		if (ok && results.length === texts.length) {
			return Array.isArray(text) ? results : results[0] || text;
		}

		console.warn("[translate] 翻译返回格式异常或翻译失败:", data);
		return text;
	} catch (error) {
		console.error("[translate] 翻译失败:", error);
		return text;
	}
}
